import {
  GraphQLID,
  GraphQLFieldConfig,
  GraphQLInputObjectType,
  GraphQLNonNull,
  GraphQLString,
  GraphQLList
} from 'graphql'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import {
  vulnerabilityFilterArgType,
  VulnerabilityFilterArg
} from '../types/vulnerability-filter-arg'
import { mapVulnerabilityFilters } from '../endpoint-helpers/vulnerabilities'

interface Context {
  readonly connector: {
    readonly post: (key: string, body?: any) => Promise<any>
  }
}

interface ApproveStatusArgs {
  readonly input?: {
    readonly organizationID: string
    readonly ids: string[]
  }
}

const approveStatusMutationArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'ApproveStatusMutationArgs',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization with the vulnerabilities being approved.',
          type: new GraphQLNonNull(GraphQLID)
        },
        ids: {
          description: 'The IDs of the vulnerabilities to be approved.',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        }
      })
    })
  }
}

export const approveVulnerabilitiesStatus: GraphQLFieldConfig<
  any,
  Context,
  ApproveStatusArgs
> = {
  description: 'Approve status change for a list of vulnerabilities',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: approveStatusMutationArgs,
  resolve: async function resolve(
    _source,
    args,
    context
  ): Promise<GenericMutationResponse> {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { organizationID, ids } = input

    const { connector } = context

    await connector.post(
      `/${organizationID}/approval-workflow/traces/pending-status`,
      {
        traces: ids,
        approved: true
      }
    )

    return { _placeholder: true }
  }
}

interface BulkApproveStatusArgs {
  readonly input?: {
    readonly organizationID: string
    readonly filters: VulnerabilityFilterArg
    readonly excludedVulnerabilities: string[]
  }
}

const bulkApproveStatusMutationArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'BulkApproveStatusMutationArgs',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization with the vulnerabilities being approved.',
          type: new GraphQLNonNull(GraphQLID)
        },
        filters: {
          type: new GraphQLNonNull(vulnerabilityFilterArgType)
        },
        excludedVulnerabilities: {
          description: 'A list of vulnerabilities to exclude from the filter',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        }
      })
    })
  }
}

export const bulkApproveVulnerabilitiesStatus: GraphQLFieldConfig<
  undefined,
  Context,
  BulkApproveStatusArgs
> = {
  type: new GraphQLNonNull(GenericMutationResponseType),
  description: 'Approve status change for based on a set of filters',
  args: bulkApproveStatusMutationArgs,
  async resolve(_source, args, context): Promise<GenericMutationResponse> {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const { filters, organizationID, excludedVulnerabilities } = args.input

    await context.connector.post(
      `/${organizationID}/approval-workflow/traces/pending-status/filters`,
      {
        filters: {
          ...mapVulnerabilityFilters(filters),
          excludedTraces: excludedVulnerabilities
        },
        approved: true
      }
    )

    return {
      _placeholder: true
    }
  }
}

interface DenyStatusArgs {
  readonly input?: {
    readonly organizationID: string
    readonly ids: string[]
    readonly comment: string
  }
}

const denyStatusMutationArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'DenyStatusMutationArgs',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization with the vulnerabilities being denied.',
          type: new GraphQLNonNull(GraphQLID)
        },
        ids: {
          description: 'The IDs of the vulnerabilities to be denied.',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        },
        comment: {
          description: 'Reasoning for denying the requested status change.',
          type: new GraphQLNonNull(GraphQLString)
        }
      })
    })
  }
}

export const denyVulnerabilitiesStatus: GraphQLFieldConfig<
  any,
  Context,
  DenyStatusArgs
> = {
  description: 'Deny status change for a list of vulnerabilities',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: denyStatusMutationArgs,
  resolve: async function resolve(
    _source,
    args,
    context
  ): Promise<GenericMutationResponse> {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { organizationID, ids, comment } = input

    const { connector } = context

    await connector.post(
      `/${organizationID}/approval-workflow/traces/pending-status`,
      {
        traces: ids,
        approved: false,
        comment
      }
    )

    return { _placeholder: true }
  }
}

interface BulkDenyStatusArgs {
  readonly input?: {
    readonly organizationID: string
    readonly filters: VulnerabilityFilterArg
    readonly excludedVulnerabilities: string[]
    readonly comment: string
  }
}

const bulkDenyStatusMutationArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'BulkDenyStatusMutationArgs',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization with the vulnerabilities being denied.',
          type: new GraphQLNonNull(GraphQLID)
        },
        filters: {
          type: new GraphQLNonNull(vulnerabilityFilterArgType)
        },
        excludedVulnerabilities: {
          description: 'A list of vulnerabilities to exclude from the filter',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        },
        comment: {
          description: 'Reasoning for denying the requested status change.',
          type: new GraphQLNonNull(GraphQLString)
        }
      })
    })
  }
}

export const bulkDenyVulnerabilitiesStatus: GraphQLFieldConfig<
  undefined,
  Context,
  BulkDenyStatusArgs
> = {
  type: new GraphQLNonNull(GenericMutationResponseType),
  description: 'Deny status change for based on a set of filters',
  args: bulkDenyStatusMutationArgs,
  async resolve(_source, args, context): Promise<GenericMutationResponse> {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const { filters, organizationID, excludedVulnerabilities, comment } =
      args.input

    await context.connector.post(
      `/${organizationID}/approval-workflow/traces/pending-status/filters`,
      {
        filters: {
          ...mapVulnerabilityFilters(filters),
          excludedTraces: excludedVulnerabilities
        },
        approved: false,
        comment
      }
    )

    return {
      _placeholder: true
    }
  }
}
